import { useEffect, useState } from "react";
import AuthForm, { AuthTermsNote, type AuthStep } from "./AuthForm";

/**
 * Sign in / sign up over the current page instead of leaving it for /get-started. Same form, same
 * options (Google, Discord, email + password) and the same return-to handling as the page.
 */
export default function AuthModal({ returnTo, source, onClose }: { returnTo?: string; source?: string; onClose: () => void }) {
  const [step, setStep] = useState<AuthStep>("email");

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center sm:p-4" role="presentation">
      <div aria-hidden="true" onClick={onClose} className="absolute inset-0 bg-black/60" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-form-title"
        className="relative flex max-h-[92vh] w-full max-w-[420px] flex-col gap-5 overflow-y-auto rounded-t-[24px] bg-[#0B0B0D] px-6 pb-7 pt-12 text-[#FAFAFA] shadow-[0_30px_80px_rgba(0,0,0,0.5)] font-[Onest,system-ui,sans-serif] sm:rounded-[24px] sm:px-8"
        data-testid="auth-modal"
      >
        <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-[200px] rounded-t-[24px] bg-[radial-gradient(50%_100%_at_50%_0%,rgba(124,58,237,0.22)_0%,rgba(124,58,237,0)_100%)]" />
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 z-[1] flex h-10 w-10 items-center justify-center rounded-full bg-[#232327] text-[#FAFAFA] transition-colors hover:bg-[#2C2C31]"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
        </button>
        <div className="relative">
          <AuthForm returnTo={returnTo} authSource={source} onStepChange={setStep} titleAs="h2" />
        </div>
        {step === "email" && (
          <div className="relative text-center">
            <AuthTermsNote />
          </div>
        )}
      </div>
    </div>
  );
}

